import { LegalShell, CompanyBlock, type LegalLang } from './legalShared'
import { useCompany } from '../hooks/useCompanyInfo'

const APP = 'Provisyer'

// "Hakkımızda" — who the seller is, what is sold, and the full legal identity.
// Required by acquiring banks before a virtual POS is activated, and by the
// Turkish e-commerce regulation (ETBIS / 6563) for any commercial website.
//
// The identity block is NOT hardcoded here: it renders whatever an admin has
// entered under /platform-settings, so the trade name, address, tax office and
// MERSİS number have exactly one source of truth.
export default function AboutPage({ lang = 'tr' }: { lang?: LegalLang }) {
  return lang === 'en' ? <AboutEN /> : <AboutTR />
}

function AboutTR() {
  const company = useCompany()
  return (
    <LegalShell lang="tr" page="about" title="Hakkımızda" updated="Son güncelleme: 13 Ağustos 2026">
      <p>
        {APP}, işletmelerin Odoo tabanlı kurumsal kaynak planlama (ERP) kurulumlarını dakikalar içinde ayağa
        kaldırmasını, yönetmesini ve ölçeklendirmesini sağlayan bir bulut hizmetidir. Sunucu kurulumu, güncelleme,
        yedekleme ve güvenlik gibi teknik yükleri biz üstleniriz; siz yalnızca işinizi yönetirsiniz.
      </p>
      <p>
        {APP} bağımsız bir barındırma ve yönetim hizmetidir. Odoo S.A. veya iştirakleri ile herhangi bir ortaklık,
        bayilik veya resmi bağlantımız bulunmamaktadır. Odoo, Odoo S.A. şirketinin tescilli markasıdır.
      </p>

      <h2>1. Ne Satıyoruz</h2>
      <p>
        Hizmetlerimiz dijital abonelik ürünleridir; fiziksel ürün satışı yapılmaz. Güncel ürünler ve fiyatlar
        <a href="/urunler"> Ürünler</a> sayfasında listelenir.
      </p>
      <ul>
        <li>
          <strong>Yönetilen Odoo kurulumu:</strong> size özel Odoo örneği, ayrı veritabanı, izole Docker ağı, otomatik
          yedekleme, özel alan adı ve SSL sertifikası, modül kurulum ve güncelleme yönetimi. Aylık veya yıllık abonelik.
        </li>
        <li>
          <strong>Mail sunucu eklentisi:</strong> kendi alan adınızdan kurumsal e-posta gönderim ve alım altyapısı
          (SPF, DKIM, DMARC yapılandırması dâhil). Yönetilen Odoo kurulumuna ek olarak satılır.
        </li>
        <li>
          <strong>Kurumsal çözümler:</strong> özel modül geliştirme, SLA, adanmış kaynak ve yerinde kurulum
          seçenekleri. Teklif usulü fiyatlandırılır.
        </li>
      </ul>

      <h2>2. Nasıl Çalışıyoruz</h2>
      <p>
        Satın alma işlemi tamamen çevrim içidir. Ürünü sepete eklersiniz, sepet sayfasında toplam tutarı ve Mesafeli
        Satış Sözleşmesi&apos;ni onaylarsınız, ödemeyi 3D Secure ile bankanızın güvenli ödeme sayfasında yaparsınız.
        Kart bilgileriniz hiçbir aşamada sunucularımıza gelmez veya tarafımızca saklanmaz. Ödeme onaylandığı anda
        hizmetiniz otomatik olarak kurulur ve panelinizden erişilebilir hâle gelir.
      </p>
      <p>
        Abonelikler, siz iptal edene kadar seçtiğiniz dönemde (aylık veya yıllık) otomatik olarak yenilenir. İptal ve
        iade koşulları için <a href="/teslimat-ve-iade">Teslimat ve İade</a> sayfamıza bakabilirsiniz.
      </p>

      <h2 id="iletisim">3. Satıcı ve İletişim Bilgileri</h2>
      <p>Hizmeti sunan ve satışı gerçekleştiren tüzel kişi aşağıdadır. Her konuda bu bilgilerden bize ulaşabilirsiniz.</p>
      <CompanyBlock lang="tr" company={company} />
      <p>
        Talep, şikâyet ve destek başvurularınızı yukarıdaki e-posta adresine iletebilirsiniz. Başvurularınıza en geç
        yasal süreler içinde yanıt veririz.
      </p>
    </LegalShell>
  )
}

function AboutEN() {
  const company = useCompany()
  return (
    <LegalShell lang="en" page="about" title="About Us" updated="Last updated: 13 August 2026">
      <p>
        {APP} is a cloud service that lets businesses launch, manage and scale Odoo-based ERP deployments in minutes.
        We take on the operational load - provisioning, upgrades, backups and security - so you can focus on running
        your business.
      </p>
      <p>
        {APP} is an independent hosting and management service. We are not a partner, reseller or affiliate of Odoo
        S.A. Odoo is a registered trademark of Odoo S.A.
      </p>

      <h2>1. What We Sell</h2>
      <p>
        All of our products are digital subscription services; we do not sell physical goods. Current products and
        prices are listed on the <a href="/products">Products</a> page.
      </p>
      <ul>
        <li>
          <strong>Managed Odoo instance:</strong> a dedicated Odoo deployment with its own database, isolated Docker
          network, automated backups, custom domain and SSL certificate, and managed module installation. Billed
          monthly or yearly.
        </li>
        <li>
          <strong>Mail server add-on:</strong> business email sending and receiving from your own domain, including
          SPF, DKIM and DMARC configuration. Sold as an add-on to a managed instance.
        </li>
        <li>
          <strong>Enterprise solutions:</strong> custom module development, SLA, dedicated resources and on-premise
          options. Priced on request.
        </li>
      </ul>

      <h2>2. How It Works</h2>
      <p>
        Purchasing is fully online. You add a product to your cart, review the total and approve the Distance Sales
        Agreement on the cart page, then pay through your bank&apos;s 3D Secure page. Card details never reach our
        servers and are never stored by us. As soon as payment is confirmed, your service is provisioned automatically
        and becomes available in your dashboard.
      </p>
      <p>
        Subscriptions renew automatically on the cycle you chose (monthly or yearly) until you cancel. See{' '}
        <a href="/delivery-and-returns">Delivery and Returns</a> for cancellation and refund terms.
      </p>

      <h2 id="contact">3. Seller and Contact Details</h2>
      <p>The legal entity providing the service and executing the sale is listed below.</p>
      <CompanyBlock lang="en" company={company} />
      <p>
        Requests, complaints and support enquiries can be sent to the email address above. We respond within the
        statutory time limits.
      </p>
    </LegalShell>
  )
}
